import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';
import { api } from './api';

const WelcomeToNeighborhood = ({ user, isAdmin = false }) => {
  const { user: contextUser } = useContext(AuthContext);
  const currentUser = user || contextUser;
  
  // Helper function to ensure URL has protocol
  const ensureHttpProtocol = (url) => {
    if (!url) return '';
    // If URL already has protocol, return as-is
    if (url.match(/^https?:\/\//i)) return url;
    // If URL starts with //, return as-is (protocol-relative)
    if (url.startsWith('//')) return url;
    // Otherwise, add https://
    return `https://${url}`;
  };
  
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [showAddNegotiated, setShowAddNegotiated] = useState(false);
  const [showEditAmenities, setShowEditAmenities] = useState(false);
  const [availableAmenities, setAvailableAmenities] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [customAmenity, setCustomAmenity] = useState('');
  
  // HOA Settings State
  const [hoaSettings, setHoaSettings] = useState({
    hoa_name: '',
    mailing_address: '',
    hoa_email: '',
    phone: '',
    website: '',
    website_type: 'external'
  });
  const [publicPageUrl, setPublicPageUrl] = useState('');
  const [arcEmail, setArcEmail] = useState('');
  
  // New state for amenity hours
  const [amenityTypes, setAmenityTypes] = useState([]);
  const [amenitySettings, setAmenitySettings] = useState({
    has_clubhouse: true,
    amenities_enabled: true
  });

  // Form states
  const [newService, setNewService] = useState({
    name: '',
    category: 'utility',
    website_url: '',
    phone: '',
    description: '',
    is_hoa_negotiated: false
  });

  const [newNegotiated, setNewNegotiated] = useState({
    service_name: '',
    improvement_description: '',
    effective_date: '',
    details: ''
  });

  useEffect(() => {
    fetchCommunityResources();
    fetchAvailableAmenities();
    fetchHoaSettings();
    fetchArcEmail();
    if (currentUser?.hoa_id) {
      fetchAmenityHours();
    }
  }, [currentUser?.hoa_id]);

  const fetchArcEmail = async () => {
    try {
      const response = await api.get('/api/settings/hoa-track/public', {
        params: { hoa_id: currentUser?.hoa_id }
      });
      if (response.data?.settings?.arc_email) {
        setArcEmail(response.data.settings.arc_email);
      }
    } catch (error) {
      console.log('ARC email not configured or settings not available');
    }
  };

  const fetchHoaSettings = async () => {
    try {
      const response = await api.get(`/api/hoa/${currentUser?.hoa_id}/settings`);
      if (response.data?.success && response.data?.settings) {
        setHoaSettings(response.data.settings);
        console.log('HOA settings loaded in Neighborhood:', response.data.settings);
      }
      
      // Also fetch public page URL if website_type is 'public'
      if (response.data?.settings?.website_type === 'public') {
        const publicResponse = await api.get(`/api/public-profile/${currentUser?.hoa_id}`);
        if (publicResponse.data?.slug) {
          setPublicPageUrl(`${window.location.origin}/p/${publicResponse.data.slug}`);
        }
      }
    } catch (error) {
      console.error('Failed to fetch HOA settings in Neighborhood:', error);
    }
  };

  const fetchCommunityResources = async () => {
    try {
      const response = await axios.get('/api/community/resources');
      setResources(response.data.resources);
      setSelectedAmenities(response.data.resources.amenities || []);
    } catch (error) {
      console.error('Failed to fetch community resources:', error);
      setError('Failed to load community resources');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAmenities = async () => {
    try {
      const response = await axios.get('/api/community/available-amenities');
      setAvailableAmenities(response.data.available_amenities || []);
    } catch (error) {
      console.error('Failed to fetch available amenities:', error);
    }
  };

  const fetchAmenityHours = async () => {
    try {
      // Fetch amenity types with hours
      const response = await api.get('/api/amenities/types', {
        params: { hoa_id: currentUser.hoa_id }
      });
      
      if (response.data.success) {
        const activeAmenities = (response.data.amenity_types || [])
          .filter(amenity => amenity.is_active);
        setAmenityTypes(activeAmenities);
      }
      
      // Fetch HOA settings to check if amenities are enabled and if there's a clubhouse
      // This would come from the amenity management settings
      setAmenitySettings({
        has_clubhouse: true, // This should come from amenity settings
        amenities_enabled: true // This should come from HOA settings
      });
    } catch (error) {
      console.error('Failed to fetch amenity hours:', error);
    }
  };

  const updateAmenities = async () => {
    try {
      await axios.post('/api/board/community/amenities', {
        amenities: selectedAmenities
      });
      await fetchCommunityResources();
      setShowEditAmenities(false);
    } catch (error) {
      console.error('Failed to update amenities:', error);
      setError('Failed to update amenities');
    }
  };

  const toggleAmenity = (amenity) => {
    setSelectedAmenities(prev => 
      prev.includes(amenity) 
        ? prev.filter(a => a !== amenity)
        : [...prev, amenity]
    );
  };

  const addCustomAmenity = () => {
    if (customAmenity.trim()) {
      const trimmedAmenity = customAmenity.trim();
      if (!selectedAmenities.includes(trimmedAmenity)) {
        setSelectedAmenities(prev => [...prev, trimmedAmenity]);
      }
      setCustomAmenity('');
      // In a real app, you might also want to notify the support team
      console.log(`Custom amenity "${trimmedAmenity}" added - would notify support team`);
    }
  };

  const addCommunityService = async () => {
    try {
      await axios.post('/api/board/community/service', newService);
      await fetchCommunityResources();
      setNewService({
        name: '',
        category: 'utility',
        website_url: '',
        phone: '',
        description: '',
        is_hoa_negotiated: false
      });
      setShowAddService(false);
    } catch (error) {
      console.error('Failed to add service:', error);
      setError('Failed to add community service');
    }
  };

  const addNewlyNegotiated = async () => {
    try {
      await axios.post('/api/board/community/negotiated', newNegotiated);
      await fetchCommunityResources();
      setNewNegotiated({
        service_name: '',
        improvement_description: '',
        effective_date: '',
        details: ''
      });
      setShowAddNegotiated(false);
    } catch (error) {
      console.error('Failed to add negotiated service:', error);
      setError('Failed to add newly negotiated service');
    }
  };

  const removeService = async (serviceId) => {
    try {
      await axios.delete(`/api/board/community/service/${serviceId}`);
      await fetchCommunityResources();
    } catch (error) {
      console.error('Failed to remove service:', error);
      setError('Failed to remove service');
    }
  };

  const removeNegotiated = async (negotiatedId) => {
    try {
      await axios.delete(`/api/board/community/negotiated/${negotiatedId}`);
      await fetchCommunityResources();
    } catch (error) {
      console.error('Failed to remove negotiated item:', error);
      setError('Failed to remove negotiated item');
    }
  };

  const getAmenityIcon = (amenity) => {
    const amenityName = amenity.toLowerCase();
    
    if (amenityName.includes('pool') || amenityName.includes('swimming')) return '🏊‍♂️';
    if (amenityName.includes('tennis')) return '🎾';
    if (amenityName.includes('basketball')) return '🏀';
    if (amenityName.includes('fitness') || amenityName.includes('gym')) return '💪';
    if (amenityName.includes('clubhouse')) return '🏛️';
    if (amenityName.includes('playground')) return '🛝';
    if (amenityName.includes('trail') || amenityName.includes('walking')) return '🚶‍♂️';
    if (amenityName.includes('dog park')) return '🐕';
    if (amenityName.includes('golf')) return '⛳';
    if (amenityName.includes('hot tub') || amenityName.includes('spa')) return '🛁';
    if (amenityName.includes('beach')) return '🏖️';
    if (amenityName.includes('marina') || amenityName.includes('boat') || amenityName.includes('dock')) return '⛵';
    if (amenityName.includes('business center')) return '💼';
    if (amenityName.includes('library') || amenityName.includes('study')) return '📚';
    if (amenityName.includes('game room')) return '🎮';
    if (amenityName.includes('movie') || amenityName.includes('theater')) return '🎬';
    if (amenityName.includes('bbq') || amenityName.includes('picnic')) return '🍖';
    if (amenityName.includes('garden') || amenityName.includes('green space')) return '🌱';
    if (amenityName.includes('parking')) return '🅿️';
    if (amenityName.includes('security')) return '🔒';
    if (amenityName.includes('concierge')) return '🛎️';
    if (amenityName.includes('package')) return '📦';
    if (amenityName.includes('car wash')) return '🚗';
    if (amenityName.includes('ev charging') || amenityName.includes('electric')) return '🔌';
    
    // Default icon for unknown amenities
    return '🏠';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'utility':
        return '⚡';
      case 'internet':
        return '🌐';
      case 'trash':
        return '🗑️';
      case 'emergency':
        return '🚨';
      default:
        return '🏢';
    }
  };

  const getCategoryName = (category) => {
    switch (category) {
      case 'utility':
        return 'Utilities';
      case 'internet':
        return 'Internet & Cable';
      case 'trash':
        return 'Trash & Recycling';
      case 'emergency':
        return 'Emergency Services';
      default:
        return 'Other Services';
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className="mt-2 text-sm text-red-600">{error}</p>
          <button
            onClick={fetchCommunityResources}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const groupedServices = resources?.community_services?.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {}) || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              🏘️ Welcome to {hoaSettings.hoa_name || 'the Neighborhood'}
            </h1>
            {hoaSettings.mailing_address && (
              <p className="text-gray-700 mb-1">
                <span className="font-medium">Address:</span> {hoaSettings.mailing_address}
              </p>
            )}
            {hoaSettings.hoa_email && (
              <p className="text-gray-700 mb-1">
                <span className="font-medium">Email:</span> {hoaSettings.hoa_email}
              </p>
            )}
            {hoaSettings.phone && (
              <p className="text-gray-700 mb-1">
                <span className="font-medium">Phone:</span> {hoaSettings.phone}
              </p>
            )}
            {arcEmail && (
              <p className="text-gray-700 mb-1">
                <span className="font-medium">ARC Committee:</span>{' '}
                <a href={`mailto:${arcEmail}`} className="text-blue-600 hover:text-blue-800">
                  {arcEmail}
                </a>
              </p>
            )}
            {(hoaSettings.website || hoaSettings.website_type === 'public') && (
              <p className="text-gray-700">
                <span className="font-medium">Website:</span>{' '}
                {hoaSettings.website_type === 'public' && publicPageUrl ? (
                  <button
                    onClick={() => window.open(publicPageUrl, '_blank', 'noopener,noreferrer')}
                    className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-0 p-0 font-inherit"
                  >
                    Visit Our Public Page
                  </button>
                ) : (
                  <a 
                    href={hoaSettings.website} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {hoaSettings.website}
                  </a>
                )}
              </p>
            )}
            <p className="text-gray-600 text-base">
              Your complete guide to community amenities and essential services
            </p>
          </div>
          {isAdmin && (
            <div className="flex space-x-2">
              <button
                onClick={() => setShowAddService(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Add Service
              </button>
              <button
                onClick={() => setShowAddNegotiated(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Add Update
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* HOA Amenities Section */}
      {resources?.amenities && resources.amenities.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  🏊‍♂️ What Your HOA Offers
                </h2>
                <p className="text-sm text-gray-600 mt-1">Amenities provided directly by your HOA</p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowEditAmenities(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-md text-sm font-medium"
                >
                  Edit Amenities
                </button>
              )}
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {resources.amenities.map((amenity, index) => (
                <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <span className="text-2xl mb-2 block">{getAmenityIcon(amenity)}</span>
                  <p className="text-sm font-medium text-green-800">{amenity}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Amenity Hours Section */}
      {amenitySettings.amenities_enabled && amenityTypes.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              🕐 <u>Hours</u>
            </h2>
            <p className="text-sm text-gray-600 mt-1">Amenity hours and availability</p>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {/* Clubhouse first if enabled */}
              {amenitySettings.has_clubhouse && 
               amenityTypes.find(a => a.name.toLowerCase().includes('clubhouse')) && (
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <div className="flex items-center">
                    <span className="text-lg mr-3">🏢</span>
                    <span className="font-medium text-gray-900">Clubhouse</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {(() => {
                      const clubhouse = amenityTypes.find(a => a.name.toLowerCase().includes('clubhouse'));
                      if (clubhouse?.always_open) return '24/7';
                      return `${clubhouse?.hours_start || '08:00'} - ${clubhouse?.hours_end || '22:00'}`;
                    })()}
                  </div>
                </div>
              )}
              
              {/* Other amenities */}
              {amenityTypes
                .filter(amenity => !amenity.name.toLowerCase().includes('clubhouse'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((amenity, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">
                        {getAmenityIcon(amenity.name)}
                      </span>
                      <span className="font-medium text-gray-900">{amenity.name}</span>
                      {amenity.capacity > 1 && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          Capacity: {amenity.capacity}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {amenity.always_open ? '24/7' : `${amenity.hours_start} - ${amenity.hours_end}`}
                    </div>
                  </div>
                ))}
            </div>
            
            {amenityTypes.length === 0 && (
              <div className="text-center py-6 text-gray-500">
                <span className="text-2xl block mb-2">🏊‍♂️</span>
                <p className="text-sm">No amenity hours available</p>
                <p className="text-xs mt-1">Check with your board for amenity information</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Newly Negotiated Services */}
      {resources?.newly_negotiated && resources.newly_negotiated.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              ✨ Newly Negotiated & Recent Updates
            </h2>
            <p className="text-sm text-gray-600 mt-1">Latest improvements and deals for our community</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {resources.newly_negotiated.map((item, index) => (
                <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-lg mr-2">🎉</span>
                        <h3 className="font-semibold text-gray-900">{item.service_name}</h3>
                      </div>
                      <p className="text-gray-700 mb-2">{item.improvement_description}</p>
                      {item.details && (
                        <p className="text-sm text-gray-600 mb-2">{item.details}</p>
                      )}
                      {item.effective_date && (
                        <p className="text-xs text-gray-500">
                          Effective: {new Date(item.effective_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => removeNegotiated(item.id)}
                        className="text-red-600 hover:text-red-800 ml-4"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Community Services */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            🔧 Essential Community Services
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {resources?.amenities && resources.amenities.length > 0 
              ? "Additional services for all your needs"
              : "Sign up for all your essential services - we've got you covered!"
            }
          </p>
        </div>
        <div className="p-6">
          {Object.keys(groupedServices).length === 0 ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h10M7 15h10" />
              </svg>
              <p className="mt-2 text-sm text-gray-500">No community services added yet</p>
              {isAdmin && (
                <button
                  onClick={() => setShowAddService(true)}
                  className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm"
                >
                  Add First Service
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedServices).map(([category, services]) => (
                <div key={category}>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <span className="text-2xl mr-2">{getCategoryIcon(category)}</span>
                    {getCategoryName(category)}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {services.map((service, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <h4 className="font-semibold text-gray-900">{service.name}</h4>
                              {service.is_hoa_negotiated && (
                                <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                  HOA Rate
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                            <div className="flex flex-wrap gap-2">
                              {service.website_url && (
                                <a
                                  href={ensureHttpProtocol(service.website_url)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 text-sm font-medium rounded-full transition-colors"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.559-.499-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.559.499.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.497-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                                  </svg>
                                  Visit Website
                                </a>
                              )}
                              {service.phone && (
                                <a
                                  href={`tel:${service.phone}`}
                                  className="inline-flex items-center px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 text-sm font-medium rounded-full transition-colors"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                  </svg>
                                  {service.phone}
                                </a>
                              )}
                            </div>
                          </div>
                          {isAdmin && (
                            <button
                              onClick={() => removeService(service.id)}
                              className="text-red-600 hover:text-red-800 flex-shrink-0 ml-4"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Service Modal */}
      {showAddService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Add Community Service</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Name</label>
                <input
                  type="text"
                  value={newService.name}
                  onChange={(e) => setNewService({...newService, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., ABC Electric Company"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={newService.category}
                  onChange={(e) => setNewService({...newService, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="utility">Utilities</option>
                  <option value="internet">Internet & Cable</option>
                  <option value="trash">Trash & Recycling</option>
                  <option value="emergency">Emergency Services</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
                <input
                  type="url"
                  value={newService.website_url}
                  onChange={(e) => setNewService({...newService, website_url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={newService.phone}
                  onChange={(e) => setNewService({...newService, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={newService.description}
                  onChange={(e) => setNewService({...newService, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  rows="3"
                  placeholder="Brief description of the service"
                />
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newService.is_hoa_negotiated}
                    onChange={(e) => setNewService({...newService, is_hoa_negotiated: e.target.checked})}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">HOA has negotiated special rates</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddService(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addCommunityService}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md"
              >
                Add Service
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Newly Negotiated Modal */}
      {showAddNegotiated && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Add Recent Update</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Name</label>
                <input
                  type="text"
                  value={newNegotiated.service_name}
                  onChange={(e) => setNewNegotiated({...newNegotiated, service_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Community Internet"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Improvement Description</label>
                <textarea
                  value={newNegotiated.improvement_description}
                  onChange={(e) => setNewNegotiated({...newNegotiated, improvement_description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  rows="3"
                  placeholder="e.g., Now offers gigabit speeds at reduced rates for all residents"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Details</label>
                <textarea
                  value={newNegotiated.details}
                  onChange={(e) => setNewNegotiated({...newNegotiated, details: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  rows="2"
                  placeholder="Additional information (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Effective Date</label>
                <input
                  type="date"
                  value={newNegotiated.effective_date}
                  onChange={(e) => setNewNegotiated({...newNegotiated, effective_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddNegotiated(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addNewlyNegotiated}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md"
              >
                Add Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Amenities Modal */}
      {showEditAmenities && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Edit Community Amenities</h3>
                <button
                  onClick={() => setShowEditAmenities(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Select the amenities that your HOA provides to residents:
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {availableAmenities.map((amenity, index) => (
                  <label key={index} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedAmenities.includes(amenity)}
                      onChange={() => toggleAmenity(amenity)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-xl mr-2">{getAmenityIcon(amenity)}</span>
                    <span className="text-sm text-gray-700">{amenity}</span>
                  </label>
                ))}
              </div>
              
              {/* Custom Amenity Section */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h4 className="font-medium text-gray-800 mb-3">Don't see your amenity? Add a custom one:</h4>
                <div className="flex space-x-3">
                  <input
                    type="text"
                    placeholder="Enter custom amenity name..."
                    value={customAmenity}
                    onChange={(e) => setCustomAmenity(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    onClick={addCustomAmenity}
                    disabled={!customAmenity.trim()}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium"
                  >
                    Add Custom
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Custom amenities will be reviewed by our support team and may be added to the main list for all HOAs.
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <h4 className="font-medium text-blue-800 mb-1">Selected Amenities ({selectedAmenities.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedAmenities.map((amenity, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                      <span className="mr-1">{getAmenityIcon(amenity)}</span>
                      {amenity}
                      <button
                        onClick={() => {
                          setSelectedAmenities(prev => prev.filter(a => a !== amenity));
                        }}
                        className="ml-1 text-red-500 hover:text-red-700 focus:outline-none"
                        title={`Remove ${amenity}`}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                  {selectedAmenities.length === 0 && (
                    <span className="text-blue-600 text-sm">No amenities selected</span>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditAmenities(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={updateAmenities}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md"
                >
                  Save Amenities
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WelcomeToNeighborhood;